import React, { useEffect, useState } from "react";
import { Heading, Subheading } from "../../../../design/heading";
import { Text } from "../../../../design/text";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Badge } from "../../../../design/badge";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db, functions } from "../../../../firebase";
import { useAccount } from "../../../../contexts/AccountContext";
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";

const CURRENT_TERMS_VERSION = '2026-06-19-v1';

const EMAIL_TYPE_LABELS: Record<string, string> = {
  visit_scheduled: "Visit Scheduled",
  visit_invitation: "Visit Invitation",
  visit_response: "Visit Response",
  team_invite: "Team Invite",
  sign_in_link: "Sign-in Link",
  supervisor_welcome: "Supervisor Welcome",
  test: "Test Email",
};

// ============================================================================
// EMAIL ACTIVITY TAB
// ============================================================================

interface EmailLog {
  id: string;
  to: string;
  from: string;
  subject: string;
  type: string;
  status: string;
  error?: string;
  createdAt: string | null;
}

interface EmailStats {
  total: number;
  sent: number;
  failed: number;
  byType: Record<string, number>;
}

const EmailActivity: React.FC = () => {
  const { account } = useAccount();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account?.id) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);
        const logsQuery = query(
          collection(db, "emailLogs"),
          where("accountId", "==", account.id),
          orderBy("createdAt", "desc"),
          limit(100)
        );
        const snapshot = await getDocs(logsQuery);
        const logsList = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString() || null,
        })) as EmailLog[];

        setLogs(logsList);

        // Compute stats from fetched logs
        const s: EmailStats = { total: logsList.length, sent: 0, failed: 0, byType: {} };
        logsList.forEach(l => {
          if (l.status === "sent") s.sent++;
          if (l.status === "failed") s.failed++;
          s.byType[l.type] = (s.byType[l.type] || 0) + 1;
        });
        setStats(s);
      } catch (err: any) {
        setError(err.message || "Failed to load email activity");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [account?.id]);

  if (loading) return <Text className="py-8 text-center text-zinc-500">Loading email activity...</Text>;
  if (error) return (
    <div className="p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800">
      <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
    </div>
  );

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
            <Text className="text-sm text-zinc-500">Total Sent</Text>
            <Text className="text-2xl font-bold">{stats.total}</Text>
          </div>
          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
            <Text className="text-sm text-zinc-500">Delivered</Text>
            <Text className="text-2xl font-bold text-green-600">{stats.sent}</Text>
          </div>
          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
            <Text className="text-sm text-zinc-500">Failed</Text>
            <Text className="text-2xl font-bold text-red-600">{stats.failed}</Text>
          </div>
          <div className="p-4 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
            <Text className="text-sm text-zinc-500">Success Rate</Text>
            <Text className="text-2xl font-bold">
              {stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0}%
            </Text>
          </div>
        </div>
      )}

      {stats && Object.keys(stats.byType).length > 0 && (
        <div className="p-4 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
          <Text className="mb-3 text-sm font-medium text-zinc-500">Emails by Type</Text>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.byType).map(([type, count]) => (
              <Badge key={type} color="zinc">{EMAIL_TYPE_LABELS[type] || type}: {count}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700 overflow-hidden">
        <div className="px-4 py-3 border-b dark:border-zinc-700">
          <Text className="text-sm font-medium">Recent Activity</Text>
        </div>
        {logs.length === 0 ? (
          <div className="p-8 text-center">
            <Text className="text-zinc-500">No emails sent yet.</Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50">
                  <th className="px-4 py-2 font-medium text-left text-zinc-500">Date</th>
                  <th className="px-4 py-2 font-medium text-left text-zinc-500">To</th>
                  <th className="px-4 py-2 font-medium text-left text-zinc-500">Subject</th>
                  <th className="px-4 py-2 font-medium text-left text-zinc-500">Type</th>
                  <th className="px-4 py-2 font-medium text-left text-zinc-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-b dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/30">
                    <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{log.to}</td>
                    <td className="px-4 py-3 max-w-[250px] truncate">{log.subject}</td>
                    <td className="px-4 py-3">
                      <Badge color="zinc">{EMAIL_TYPE_LABELS[log.type] || log.type}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={log.status === "sent" ? "green" : "red"}>
                        {log.status === "sent" ? "Sent" : "Failed"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {logs.length >= 100 && (
          <div className="p-4 text-center border-t dark:border-zinc-700">
            <Text className="text-xs text-zinc-400">Showing most recent 100 emails</Text>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN EMAIL SETTINGS PAGE
// ============================================================================

const EmailSettings: React.FC = () => {
  const { account } = useAccount();
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [certChecked, setCertChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"settings" | "activity">("settings");

  // Test email state
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Read settings directly from the account document (live via context)
  const acct = account as any;
  const enabled = acct?.emailNotificationsEnabled === true;
  const termsUpToDate = acct?.emailTermsVersion === CURRENT_TERMS_VERSION;
  const enabledAt = acct?.emailEnabledAt?.toDate?.()?.toISOString() || null;
  const needsReacceptance = enabled && !termsUpToDate;
  const fullyEnabled = enabled && termsUpToDate;

  const notifyParentsVisitScheduled = acct?.notifyParentsVisitScheduled ?? false;
  const notifyParentsVisitInvitation = acct?.notifyParentsVisitInvitation ?? false;
  const notifySupervisorsVisitScheduled = acct?.notifySupervisorsVisitScheduled ?? true;
  const notifySupervisorsWelcome = acct?.notifySupervisorsWelcome ?? true;
  const requireParentConfirmation = acct?.requireParentConfirmation ?? false;

  const accountRef = doc(db, "accounts", account.id);

  const handleEnable = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Use callable for enable — it writes the audit trail
      const fn = httpsCallable(functions, "enableEmailNotifications");
      await fn({ certificationAccepted: certChecked, termsAccepted: termsChecked });
      setSuccess("Email notifications enabled successfully.");
      setCertChecked(false);
      setTermsChecked(false);
    } catch (err: any) {
      setError(err.message || "Failed to enable notifications");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreference = async (key: string, value: boolean) => {
    try {
      const update: Record<string, any> = { [key]: value };
      // Sync parentInvitationEmails when toggling parent invitation preference
      if (key === "notifyParentsVisitInvitation") {
        update.parentInvitationEmails = value;
      }
      await updateDoc(accountRef, update);
    } catch (err: any) {
      setError(err.message || "Failed to update preference");
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable email notifications? Parents and supervisors will no longer receive email notifications.")) {
      return;
    }

    setDisabling(true);
    setError(null);
    setSuccess(null);

    try {
      // Use callable for disable — it writes the audit trail
      const fn = httpsCallable(functions, "disableEmailNotifications");
      await fn();
      setSuccess("Email notifications disabled.");
    } catch (err: any) {
      setError(err.message || "Failed to disable notifications");
    } finally {
      setDisabling(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      setTestResult({ type: "error", message: "Enter an email address to send a test to" });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(functions, "sendTestEmail");
      await fn({ recipientEmail: testEmail.trim() });
      setTestResult({ type: "success", message: `Test email sent to ${testEmail}. Check your inbox!` });
    } catch (err: any) {
      setTestResult({ type: "error", message: err.message || "Failed to send test email" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="px-4 py-8 mx-auto max-w-4xl sm:px-6 lg:px-8">
      <div className="mb-6">
        <Heading level={1}>Email Notifications</Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          VisitProof can send service-related email notifications to parents and other case participants on behalf of your organization.
        </Text>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 mb-6 bg-zinc-100 dark:bg-zinc-800 rounded-lg w-fit">
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === "settings"
              ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
              : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
          }`}
        >
          Settings
        </button>
        <button
          onClick={() => setActiveTab("activity")}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === "activity"
              ? "bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white shadow-sm"
              : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
          }`}
        >
          Activity
        </button>
      </div>

      {activeTab === "activity" ? (
        <EmailActivity />
      ) : (
        <div className="space-y-6">
          {error && (
            <div className="p-4 border border-red-200 rounded-lg bg-red-50 dark:bg-red-900/20 dark:border-red-800">
              <Text className="text-sm text-red-700 dark:text-red-300">{error}</Text>
            </div>
          )}

          {success && (
            <div className="p-4 border border-green-200 rounded-lg bg-green-50 dark:bg-green-900/20 dark:border-green-800">
              <Text className="text-sm text-green-700 dark:text-green-300">{success}</Text>
            </div>
          )}

          {/* Re-acceptance banner */}
          {needsReacceptance && (
            <div className="p-4 border border-amber-200 rounded-lg bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <Text className="text-sm font-medium text-amber-800 dark:text-amber-200">Terms of Service Updated</Text>
                <Text className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  VisitProof's Terms of Service have been updated. Email notifications are paused until an organization owner accepts the new terms.
                </Text>
              </div>
            </div>
          )}

          {/* Status card */}
          <div className="p-6 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
            <div className="flex items-center justify-between mb-4">
              <Subheading>Email Notification Status</Subheading>
              {fullyEnabled ? (
                <Badge color="green">Enabled</Badge>
              ) : needsReacceptance ? (
                <Badge color="yellow">Paused</Badge>
              ) : (
                <Badge color="zinc">Disabled</Badge>
              )}
            </div>

            {fullyEnabled ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <ShieldCheckIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <Text className="text-sm text-green-800 dark:text-green-200">
                      Service-related email notifications are active. VisitProof will send emails on behalf of your organization.
                    </Text>
                    {enabledAt && (
                      <Text className="mt-1 text-xs text-green-600 dark:text-green-400">
                        Enabled on {new Date(enabledAt).toLocaleDateString()}
                      </Text>
                    )}
                  </div>
                </div>
                <Button plain onClick={handleDisable} disabled={disabling}>
                  {disabling ? "Disabling..." : "Disable Email Notifications"}
                </Button>
              </div>
            ) : null}
          </div>

          {/* Preferences — only when enabled */}
          {fullyEnabled && (
            <div className="p-6 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
              <Subheading className="mb-4">Notification Preferences</Subheading>

              <div className="mb-6">
                <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">Supervisors / Monitors</Text>
                <div className="space-y-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <Text className="text-sm text-zinc-900 dark:text-white">Visit scheduled notification</Text>
                      <Text className="text-xs text-zinc-500 dark:text-zinc-400">Email supervisors when a new visit is assigned to them</Text>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifySupervisorsVisitScheduled}
                      onChange={(e) => handleTogglePreference("notifySupervisorsVisitScheduled", e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <Text className="text-sm text-zinc-900 dark:text-white">Welcome email</Text>
                      <Text className="text-xs text-zinc-500 dark:text-zinc-400">Send app download link when a new supervisor is added</Text>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifySupervisorsWelcome}
                      onChange={(e) => handleTogglePreference("notifySupervisorsWelcome", e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </div>

              <div className="pt-4 border-t dark:border-zinc-700">
                <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">Parents / Case Participants</Text>
                <div className="space-y-4">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <Text className="text-sm text-zinc-900 dark:text-white">Email parents when visits are scheduled</Text>
                      <Text className="text-xs text-zinc-500 dark:text-zinc-400">Send a notification email to parents when a visit is added to the schedule</Text>
                    </div>
                    <input
                      type="checkbox"
                      checked={notifyParentsVisitScheduled || notifyParentsVisitInvitation}
                      onChange={(e) => {
                        handleTogglePreference("notifyParentsVisitScheduled", e.target.checked);
                        handleTogglePreference("notifyParentsVisitInvitation", e.target.checked);
                      }}
                      className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>

                  {/* Confirmation requirement — only visible when parent emails are on */}
                  {(notifyParentsVisitScheduled || notifyParentsVisitInvitation) && (
                    <div className="ml-6 p-3 bg-zinc-50 dark:bg-zinc-900/50 rounded-lg">
                      <label className="flex items-center justify-between cursor-pointer">
                        <div>
                          <Text className="text-sm text-zinc-900 dark:text-white">Require parent confirmation</Text>
                          <Text className="text-xs text-zinc-500 dark:text-zinc-400">
                            Parents must accept or decline visits before they are confirmed. Visits stay in "Awaiting Confirmation" until all parents respond. This can be overridden per case.
                          </Text>
                        </div>
                        <input
                          type="checkbox"
                          checked={requireParentConfirmation}
                          onChange={(e) => handleTogglePreference("requireParentConfirmation", e.target.checked)}
                          className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Enable form */}
          {!fullyEnabled && (
            <div className="p-6 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
              <Subheading className="mb-4">{needsReacceptance ? 'Re-accept Terms' : 'Enable Email Notifications'}</Subheading>
              <div className="space-y-6">
                <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                  Before enabling this feature, you must confirm that you are authorized to contact these individuals and have obtained any required consent.
                </Text>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={certChecked}
                    onChange={(e) => setCertChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Text className="text-sm text-zinc-700 dark:text-zinc-300">
                    I certify that I am authorized to contact this individual and have obtained any required consent for service-related communications.
                  </Text>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsChecked}
                    onChange={(e) => setTermsChecked(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Text className="text-sm text-zinc-700 dark:text-zinc-300">
                    I accept the{" "}
                    <a href="https://visitproof.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                      VisitProof Terms of Service
                    </a>
                    {" "}and authorize VisitProof to send service-related emails on behalf of my organization.
                  </Text>
                </label>

                <Button
                  onClick={handleEnable}
                  disabled={saving || !certChecked || !termsChecked}
                >
                  {saving ? "Enabling..." : "Accept Terms and Enable Email Notifications"}
                </Button>

                {!certChecked && !termsChecked && (
                  <Text className="text-xs text-zinc-400">
                    Both the certification and Terms of Service must be accepted to enable email notifications.
                  </Text>
                )}
              </div>
            </div>
          )}

          {/* Test email */}
          {fullyEnabled && (
            <div className="p-6 bg-white border rounded-lg dark:bg-zinc-800 dark:border-zinc-700">
              <Subheading className="mb-3">Send Test Email</Subheading>
              <Text className="mb-4 text-sm text-zinc-500">
                Verify that emails are being sent correctly on behalf of your organization.
              </Text>
              {testResult && (
                <div className={`p-3 mb-4 border rounded-lg ${testResult.type === "success"
                  ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                  : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"}`}
                >
                  <Text className={`text-sm ${testResult.type === "success"
                    ? "text-green-700 dark:text-green-300"
                    : "text-red-700 dark:text-red-300"}`}
                  >
                    {testResult.message}
                  </Text>
                </div>
              )}
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1"
                />
                <Button onClick={handleSendTest} disabled={testing}>
                  {testing ? "Sending..." : "Send Test"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailSettings;
